import React, { useState } from "react";
import axios from "axios";

const AddUserByIdModal = ({ onClose, onUserAdded, businessToken }) => {
  const [formData, setFormData] = useState({
    userUniqueId: "",
    relationshipType: "customer",
    notes: "",
    viewProfile: true,
    viewOrders: true,
    sendNotifications: true,
  });
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear search result if user ID changes
    if (name === "userUniqueId") {
      setSearchResult(null);
      setError("");
    }
  };

  const searchUser = async () => {
    if (!formData.userUniqueId || formData.userUniqueId.length !== 6) {
      setError("User ID must be exactly 6 characters");
      return;
    }

    setIsSearching(true);
    setError("");

    try {
      const response = await axios.get(
        `https://dailybackend-zvvt.onrender.com/api/business-user-relations/search-user/${formData.userUniqueId.toUpperCase()}`,
        {
          headers: { Authorization: `Bearer ${businessToken}` },
        }
      );

      setSearchResult(response.data);

      if (response.data.alreadyAssociated) {
        setError("This user is already associated with your business");
      } else {
        setSuccess(
          `User found: ${response.data.user.firstName} ${response.data.user.lastName}`
        );
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError("User not found with this unique ID");
      } else {
        setError(err.response?.data?.message || "Error searching for user");
      }
      setSearchResult(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!searchResult || searchResult.alreadyAssociated) {
      setError("Please search for a valid user first");
      return;
    }

    setIsAdding(true);
    setError("");

    try {
      const response = await axios.post(
        "https://dailybackend-zvvt.onrender.com/api/business-user-relations/add-user",
        formData,
        {
          headers: { Authorization: `Bearer ${businessToken}` },
        }
      );

      setSuccess("User added to your business successfully!");

      // Notify parent component
      if (onUserAdded) {
        onUserAdded(response.data.relation);
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || "Error adding user to business");
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="profile-edit-overlay">
      <div className="profile-edit-modal">
        <div className="modal-header">
          <h2>Add User to Business</h2>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="profile-edit-form">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="form-section">
            <h3>Find User by Unique ID</h3>
            <p
              style={{ color: "#666", fontSize: "14px", marginBottom: "15px" }}
            >
              Enter the 6-character unique ID of the user you want to add to
              your business.
            </p>

            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>User Unique ID</label>
                <input
                  type="text"
                  name="userUniqueId"
                  value={formData.userUniqueId}
                  onChange={handleInputChange}
                  placeholder="E.g., F33HJ3"
                  maxLength="6"
                  style={{
                    textTransform: "uppercase",
                    fontFamily: "monospace",
                  }}
                  required
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>&nbsp;</label>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={searchUser}
                  disabled={isSearching || formData.userUniqueId.length !== 6}
                  style={{ width: "100%" }}
                >
                  {isSearching ? "Searching..." : "Search User"}
                </button>
              </div>
            </div>

            {searchResult && !searchResult.alreadyAssociated && (
              <div
                style={{
                  backgroundColor: "#e8f5e8",
                  border: "1px solid #4caf50",
                  borderRadius: "6px",
                  padding: "15px",
                  marginTop: "15px",
                }}
              >
                <h4 style={{ color: "#2e7d32", margin: 0 }}>User Found!</h4>
                <p style={{ margin: "5px 0", color: "#2e7d32" }}>
                  <strong>Name:</strong> {searchResult.user.firstName}{" "}
                  {searchResult.user.lastName}
                </p>
                <p style={{ margin: "5px 0", color: "#2e7d32" }}>
                  <strong>Username:</strong> {searchResult.user.username}
                </p>
                <p style={{ margin: "5px 0", color: "#2e7d32" }}>
                  <strong>Email:</strong> {searchResult.user.email}
                </p>
              </div>
            )}

            {searchResult?.alreadyAssociated && (
              <div
                style={{
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "6px",
                  padding: "15px",
                  marginTop: "15px",
                }}
              >
                <h4 style={{ color: "#856404", margin: 0 }}>
                  User Already Associated
                </h4>
                <p style={{ margin: "5px 0", color: "#856404" }}>
                  This user is already part of your business network.
                </p>
              </div>
            )}
          </div>

          {searchResult && !searchResult.alreadyAssociated && (
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h3>Relationship Details</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>Relationship Type</label>
                    <select
                      name="relationshipType"
                      value={formData.relationshipType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="customer">Customer</option>
                      <option value="member">Member</option>
                      <option value="subscriber">Subscriber</option>
                      <option value="client">Client</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes (Optional)</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Add any notes about this relationship..."
                    maxLength="500"
                  />
                  <small style={{ color: "#666" }}>
                    {formData.notes.length}/500 characters
                  </small>
                </div>
              </div>

              <div className="form-section">
                <h3>Permissions</h3>
                <p
                  style={{
                    color: "#666",
                    fontSize: "14px",
                    marginBottom: "15px",
                  }}
                >
                  Choose what information you can access for this user.
                </p>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="viewProfile"
                      checked={formData.viewProfile}
                      onChange={handleInputChange}
                    />
                    View user profile information
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="viewOrders"
                      checked={formData.viewOrders}
                      onChange={handleInputChange}
                    />
                    View user orders and activity
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="sendNotifications"
                      checked={formData.sendNotifications}
                      onChange={handleInputChange}
                    />
                    Send notifications to user
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={isAdding}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isAdding}
                >
                  {isAdding ? "Adding User..." : "Add User to Business"}
                </button>
              </div>
            </form>
          )}

          {!searchResult && (
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddUserByIdModal;
